// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Room identity + invite-link handling. All pure / deterministic given the RNG,
 * so it is unit-tested directly. The passphrase and room id live ONLY in the URL
 * hash fragment, which browsers never transmit to a server.
 */
import { WORDS } from './wordlist.ts';
import type { Invite } from './types.ts';

const ID_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789'; // no look-alikes (l,o,0,1)

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  globalThis.crypto.getRandomValues(b);
  return b;
}

/** A short, unguessable room id (opaque to relays). */
export function generateRoomId(len = 10): string {
  const bytes = randomBytes(len);
  let out = '';
  for (let i = 0; i < len; i++) out += ID_ALPHABET[bytes[i] % ID_ALPHABET.length];
  return out;
}

/** A human-readable passphrase — four words, easy to read aloud. */
export function generatePassphrase(words = 4): string {
  const bytes = randomBytes(words * 2);
  const picks: string[] = [];
  for (let i = 0; i < words; i++) {
    const idx = ((bytes[i * 2] << 8) | bytes[i * 2 + 1]) % WORDS.length;
    picks.push(WORDS[idx]);
  }
  return picks.join('-');
}

/** Build a pairing URL. Pass `includePass=false` for the more-secure two-part flow. */
export function buildInviteUrl(base: string, room: string, pass: string, includePass = true): string {
  const origin = base.replace(/#.*$/, '').replace(/\/+$/, '') + '/';
  const params = new URLSearchParams();
  params.set('room', room);
  if (includePass && pass) params.set('pass', pass);
  return `${origin}#${params.toString()}`;
}

/** Parse the hash of a pairing URL (or a raw `#room=...&pass=...` string). */
export function parseInvite(hashOrUrl: string): Invite | null {
  let hash = hashOrUrl;
  const hashIdx = hashOrUrl.indexOf('#');
  if (hashIdx >= 0) hash = hashOrUrl.slice(hashIdx + 1);
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const room = params.get('room')?.trim();
  if (!room || !/^[a-z0-9]{4,64}$/i.test(room)) return null;
  const pass = params.get('pass')?.trim() || undefined;
  return { room, pass };
}

/** Normalise a user-typed passphrase for key derivation (trim, collapse space). */
export function normalizePass(pass: string): string {
  return pass.trim().replace(/\s+/g, ' ');
}

/** Validate a room id typed or pasted by the user. */
export function isValidRoomId(room: string): boolean {
  return /^[a-z0-9]{4,64}$/i.test(room.trim());
}
